import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Clock, Calendar, Users, ChevronRight, FileText, MapPin, CircleDollarSign, MessageCircle, ThumbsUp, ThumbsDown, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ServiceRequestService } from '@/services/serviceRequestService';
import { ServiceRequestWithProposals, JobProposalDatabaseStatus } from '@/types/serviceRequest';
import { FadeIn } from '@/components/Animations';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';

const MyServiceRequests: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestWithProposals[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading) {
      if (user) {
        loadServiceRequests();
      } else {
        // User not authenticated - set loading to false so UI can show empty state or redirect
        setIsLoading(false);
      }
    }
  }, [user, authLoading]);


  const loadServiceRequests = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const requests = await ServiceRequestService.getServiceRequestsById(user.id);
      setServiceRequests(requests);
    } catch (error) {
      console.error('Error loading service requests:', error);
      setError('Failed to load service requests');
      toast({
        title: "Error",
        description: "Failed to load your service requests. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleServiceRequestClick = (serviceRequest: ServiceRequestWithProposals) => {
    navigate(`/my-service-requests/${serviceRequest.id}`);
  };

  const getBusinessStatusDisplay = (proposal: any) => {
    const status = proposal.status as JobProposalDatabaseStatus;
    const hasQuote = proposal.quote !== null && proposal.quote !== undefined;
    const hasResponseMessage = proposal.response_message !== null && proposal.response_message !== undefined && proposal.response_message.trim() !== '';
    
    switch (status) {
      case 'AWAITING_RESPONSE':
        return {
          text: 'Waiting...',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          icons: []
        };
      case 'PROPOSED':
        return {
          text: 'Proposed',
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          icons: [
            ...(hasQuote ? [{ icon: CircleDollarSign, title: 'Quote provided' }] : []),
            ...(hasResponseMessage ? [{ icon: MessageCircle, title: 'Response message included' }] : [])
          ]
        };
      case 'DECLINED':
        return {
          text: 'Declined',
          color: 'text-red-700',
          bgColor: 'bg-red-50',
          icons: hasResponseMessage ? [{ icon: MessageCircle, title: 'Response message included' }] : []
        };
      case 'REQUESTED_INFO':
        return {
          text: 'Needs info',
          color: 'text-orange-700',
          bgColor: 'bg-orange-50',
          icons: [{ icon: MessageCircle, title: 'Response message included' }]
        };
      case 'USER_INTERESTED':
        return {
          text: 'You expressed interest',
          color: 'text-blue-700',
          bgColor: 'bg-blue-50',
          icons: [{ icon: ThumbsUp, title: 'You are interested' }]
        };
      case 'USER_REJECTED':
        return {
          text: 'You declined',
          color: 'text-gray-700',
          bgColor: 'bg-gray-50',
          icons: [{ icon: ThumbsDown, title: 'You declined this proposal' }]
        };
      default:
        return {
          text: 'Unknown',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          icons: []
        };
    }
  };

  const getServiceRequestStatusBadge = (status: string) => {
    switch (status) {
      case 'in_progress':
        return <Badge className="bg-yellow-500 text-white text-xs">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-green-500 text-white text-xs">Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-500 text-white text-xs">Cancelled</Badge>;
      default:
        return null; // Don't show badge for 'active' status
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Removed getUrgencyBadge - no longer needed as urgency is redundant for user

  if (authLoading || isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-6 px-4 bg-white">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading your service requests...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto py-6 px-4 bg-white">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-xl md:text-2xl font-bold mb-4">Error loading service requests</h1>
            <Button onClick={loadServiceRequests}>Try Again</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto py-8 md:py-10 px-4 bg-white"
      >
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <FadeIn direction="up" delay={0.1}>
            <div className="mb-6 md:mb-8 lg:mb-10">
              <h1 className="text-2xl lg:text-3xl font-bold mb-3 md:mb-4">
                My Service Requests
              </h1>
              <p className="text-sm lg:text-base text-muted-foreground">
                {serviceRequests.length > 0 ? (
                  <>
                    {serviceRequests.length === 1 
                      ? `You have 1 service request`
                      : `You have ${serviceRequests.length} service requests`
                    }
                    <br />
                    Click on any request to view details and proposals:
                  </>
                ) : (
                  'You haven\'t created any service requests yet.'
                )}
              </p>
            </div>
          </FadeIn>

          {/* Service Requests List */}
          <div className="space-y-4 md:space-y-6">
            {serviceRequests.map((serviceRequest, index) => (
              <FadeIn key={serviceRequest.id} direction="up" delay={0.2 + index * 0.1}>
                <Card 
                  className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 border-t-2 border-t-primary border-l border-r border-l-gray-200 border-r-gray-200 overflow-hidden bg-white cursor-pointer"
                  onClick={() => handleServiceRequestClick(serviceRequest)}
                >
                  <CardContent className="p-4 md:p-6">
                    {/* Header with Status */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1 line-clamp-2 pr-2">
                          {serviceRequest.original_request}
                        </h3>
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-500 flex-shrink-0" />
                          <span className="text-xs md:text-sm text-gray-600">
                            {formatDate(serviceRequest.created_at)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-400 ml-2 flex-shrink-0" />
                    </div>

                    {/* Business Proposals Status */}
                    {serviceRequest.proposals && serviceRequest.proposals.length > 0 && (
                      <div className="mb-4">
                        <div className="space-y-2">
                          {serviceRequest.proposals.slice(0, 3).map((proposal, proposalIndex) => {
                            const statusDisplay = getBusinessStatusDisplay(proposal);
                            
                            return (
                              <div key={proposal.id} className={`p-3 rounded-lg ${statusDisplay.bgColor}`}>
                                {/* Single Row: Logo, Business Name, Status and Icons */}
                                <div className="flex items-center justify-between gap-3">
                                  {/* Left Side: Logo and Business Name */}
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {/* Company Logo */}
                                    <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                                      {proposal.business?.company_logo ? (
                                        <img
                                          src={proposal.business.company_logo}
                                          alt={proposal.business?.name || 'Business'}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            target.nextElementSibling?.classList.remove('hidden');
                                          }}
                                        />
                                      ) : null}
                                      <div className={`w-full h-full bg-gray-300 rounded-full flex items-center justify-center ${proposal.business?.company_logo ? 'hidden' : ''}`}>
                                        <span className="text-xs font-medium text-gray-600">
                                          {proposal.business?.name?.charAt(0) || 'B'}
                                        </span>
                                      </div>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                      {proposal.business?.name && proposal.business.name.length > 20 
                                        ? `${proposal.business.name.substring(0, 20)}...` 
                                        : proposal.business?.name || 'Business'}
                                    </span>
                                  </div>
                                  
                                  {/* Right Side: Status and Icons */}
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className={`text-xs font-medium ${statusDisplay.color}`}>
                                      {statusDisplay.text}
                                    </span>
                                    {statusDisplay.icons.map((iconInfo, iconIndex) => {
                                      const IconComponent = iconInfo.icon;
                                      return (
                                        <IconComponent 
                                          key={iconIndex}
                                          className="w-3.5 h-3.5 text-gray-600 ml-1"
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {serviceRequest.proposals.length > 3 && (
                            <div className="text-center py-1">
                              <span className="text-xs text-gray-500">
                                +{serviceRequest.proposals.length - 3} more business{serviceRequest.proposals.length - 3 !== 1 ? 'es' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Service Request Status & Location */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getServiceRequestStatusBadge(serviceRequest.status)}
                        {serviceRequest.proposals?.length === 0 && (
                          <Badge className="bg-gray-500 text-white text-xs">
                            No proposals yet
                          </Badge>
                        )}
                      </div>
                      {serviceRequest.town && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span>{serviceRequest.town}</span>
                        </div>
                      )}
                    </div>

                    {/* Attachments indicator */}
                    {serviceRequest.attachments && serviceRequest.attachments.length > 0 && (
                      <div className="flex items-center gap-1 text-xs md:text-sm text-gray-500 mt-2">
                        <FileText className="w-3 h-3 md:w-3.5 md:h-3.5 flex-shrink-0" />
                        <span>{serviceRequest.attachments.length} attachment{serviceRequest.attachments.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>

          {/* Empty State */}
          {serviceRequests.length === 0 && (
            <FadeIn direction="up" delay={0.2}>
              <Card className="text-center py-6 md:py-8 border-0 shadow-lg bg-white">
                <CardContent>
                  <div className="mb-3 md:mb-4">
                    <ArrowLeftRight className="w-10 h-10 md:w-12 md:h-12 text-gray-400 mx-auto mb-2" />
                  </div>
                  <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">
                    {t('my_service_requests.no_requests.title') || 'Ready to get things done?'}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base mb-4 md:mb-6">
                    {t('my_service_requests.no_requests.description') || 'Connect with trusted local service providers in your community. Get quotes, compare options, and find the perfect match for your needs!'}
                  </p>
                  <Button 
                    onClick={() => navigate('/service-request')}
                    className="min-h-[40px] md:min-h-[44px] px-4 md:px-6 bg-primary hover:bg-primary/90 text-white text-sm md:text-base"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5 md:w-4 md:h-4 mr-2" />
                    {t('my_service_requests.no_requests.button') || 'Request a Service'}
                  </Button>
                </CardContent>
              </Card>
            </FadeIn>
          )}
        </div>

      </motion.div>
    </Layout>
  );
};

export default MyServiceRequests;